import { onlineManager } from "@tanstack/react-query";

import { get, post, httpDelete } from "@app/common/api";
import type {
  ApiUser,
  UserZoomProfile,
  SignUpUser,
  SignInUser,
  ProviderType,
} from "@app/common/types";
import type {
  CalendarProviderId,
  SSOProviderId,
} from "@app/common/types/providerTypes";
import { isResponseErrorWithStatus } from "@app/common/utils";

export interface PasswordResetBody {
  newPassword: string;
  confirmPassword: string;
  token: string | null;
}

export const signInUser = async (user: SignInUser) =>
  await post("/login/read", user, { withCreds: true });

export const secureSignInUser = async ({
  euid,
  provider,
}: {
  euid: string;
  provider: "zoom";
}) =>
  await post(
    "/login/read/provider",
    { encryptedProviderUserId: euid, provider },
    { withCreds: true }
  );

export const signUpUser = async (user: SignUpUser) => {
  try {
    return await post("/users", user, { withCreds: true });
  } catch (error) {
    if (isResponseErrorWithStatus(error, 409)) {
      return { error: true, message: "Sorry! That email is taken." };
    }
    throw error;
  }
};

export interface ConnectedAccountProvider {
  providerId: SSOProviderId;
  providerName: SSOProviderId;
  providerType: Extract<ProviderType, "login">;
  providerEmail: string | null;
  reauthenticationRequired?: boolean;
  canEditCalendar?: boolean;
  canReadEmail?: boolean;
  lastEmailSyncTime?: string;
}

export interface ConnectedCalendarProvider {
  providerId: CalendarProviderId;
  providerName: SSOProviderId;
  providerType: Extract<ProviderType, "calendar">;
  providerEmail: string;
  reauthenticationRequired?: boolean;
  canEditCalendar?: boolean;
}

export type ConnectedProvider =
  | ConnectedAccountProvider
  | ConnectedCalendarProvider;

export const getConnectedSSO = async (): Promise<ConnectedProvider[]> => {
  return await get("/users/me/sso", { withCreds: true });
};

export const deleteConnectedSSO = async (
  providerId: SSOProviderId | CalendarProviderId
): Promise<boolean> => {
  return await httpDelete(`/users/me/sso?provider=${providerId}`, {
    withCreds: true,
  });
};

export const signOutUser = async () => {
  try {
    await httpDelete("/users/me/token", { withCreds: true });
    return true;
  } catch {
    return false;
  }
};

export const getUser = async (options?: {
  authBearer?: string;
}): Promise<ApiUser> => {
  const user = (await get("/users/me", {
    withCreds: true,
    ...options,
  })) as ApiUser;

  // Update online state if we got a successful response. Note that if we
  // change the user fetch call to use useQuery, we'll want to detect
  // the online state using some other mechanism. The gist is that navigator.onLine
  // is not reliable and can return false negatives.
  onlineManager.setOnline(true);

  return user;
};

export const postRequestPasswordReset = async (
  email: string | null,
  recaptchaValue: string | null,
  withCreds = false
) => {
  return post(
    "/users/request/password-reset",
    { email, recaptchaValue },
    { withCreds }
  );
};

export const postResetPassword = async (body: PasswordResetBody) => {
  return post("/users/auth/reset-password", {
    token: body.token,
    newPassword: body.newPassword,
    confirmPassword: body.confirmPassword,
  });
};

export const deleteAccount = async () => {
  return httpDelete("/users/me", { withCreds: true });
};

export const getZoomProfile = async (): Promise<UserZoomProfile> => {
  return get("/users/me/zoom_profile", { withCreds: true });
};
